// src/business/pages/BusinessAnalytics.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DollarSign,
  ShoppingBag,
  Package,
  TrendingUp,
  RefreshCw,
} from "lucide-react";

import Panel from "../components/Panel";
import StatCard from "../components/StatCard";
import { request, ROUTES } from "../../services/api";
import { Button, PageHeader } from "../../components/ui";

/* ─── helpers ─────────────────────────────────────────── */

function money(n) {
  return `₱${Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(a, b) {
  if (!b) return a > 0 ? 100 : 0;
  return Math.round(((a - b) / b) * 100);
}
function pctLabel(val) {
  if (val === 0) return "No change vs last month";
  return `${val > 0 ? "+" : ""}${val}% vs last month`;
}

function SkeletonBlock({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-neutral-200 dark:bg-neutral-800 ${className}`}
      aria-hidden
    />
  );
}

/* ─── Horizontal Bar Chart ───────────────────────────── */

function BarChart({ items = [], labelKey, valueKey, formatValue, maxBars = 10 }) {
  const data = items.slice(0, maxBars);
  const max = Math.max(...data.map((d) => Number(d[valueKey] || 0)), 1);

  if (data.length === 0)
    return (
      <p className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
        No data available yet.
      </p>
    );

  return (
    <div className="space-y-3">
      {data.map((d, i) => {
        const val = Number(d[valueKey] || 0);
        const w = Math.max(3, Math.round((val / max) * 100));
        return (
          <div key={`${d[labelKey]}-${i}`} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-neutral-800 dark:text-neutral-100 truncate pr-2 max-w-[60%]">
                {d[labelKey] || "—"}
              </span>
              <span className="tabular-nums text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                {formatValue ? formatValue(val) : val.toLocaleString()}
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
              <div
                className="h-full rounded-full bg-indigo-500 transition-all duration-500"
                style={{ width: `${w}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── SVG Doughnut ────────────────────────────────────── */

const CHART_COLORS = [
  "rgb(var(--color-accent-text))",
  "rgb(var(--color-success-text))",
  "rgb(var(--color-warning-text))",
  "rgb(var(--color-danger-text))",
];

const STATUS_CHART_COLORS = {
  "to ship": "#F97316",
  packed: "#2563EB",
  processing: "#7C3AED",
  pending: "#CA8A04",
  shipped: "#06B6D4",
  delivered: "#059669",
  cancelled: "#E11D48",
  "out of stock": "#475569",
  "low stock (1-10)": "#C026D3",
  "in stock (>10)": "#65A30D",
};

const normalizeChartStatus = (value) => String(value || "").trim().toLowerCase().replace(/_/g, " ").replace(/\s+/g, " ");
const chartColor = (value, index) => STATUS_CHART_COLORS[normalizeChartStatus(value)] || CHART_COLORS[index % CHART_COLORS.length];

function DoughnutChart({ items = [], labelKey = "status", valueKey = "count", ariaLabel = "Breakdown chart" }) {
  const data = items.filter((d) => Number(d[valueKey]) > 0);
  const total = data.reduce((s, d) => s + Number(d[valueKey] || 0), 0) || 1;

  let cumulative = 0;
  const arcs = data.map((d, i) => {
    const val = Number(d[valueKey] || 0);
    const share = val / total;
    const start = cumulative;
    cumulative += share;
    return { ...d, val, share, start, color: chartColor(d[labelKey], i) };
  });

  function polar(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(start, end, r, cx, cy) {
    const s = polar(cx, cy, r, start * 360);
    const e = polar(cx, cy, r, end * 360);
    const large = end - start > 0.5 ? 1 : 0;
    return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
  }

  if (data.length === 0)
    return (
      <p className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
        No order data yet.
      </p>
    );

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <svg viewBox="0 0 100 100" className="h-36 w-36 shrink-0" role="img" aria-label={ariaLabel}>
        {arcs.map((a, i) => (
          <path
            key={i}
            d={arcPath(a.start, a.start + a.share - 0.003, 38, 50, 50)}
            fill="none"
            stroke={a.color}
            strokeWidth="14"
            strokeLinecap="round"
          />
        ))}
        <text x="50" y="47" textAnchor="middle" className="fill-neutral-800 dark:fill-neutral-100" fontSize="12" fontWeight="700">
          {total}
        </text>
        <text x="50" y="58" textAnchor="middle" className="fill-neutral-500 dark:fill-neutral-400" fontSize="7">
          Orders
        </text>
      </svg>
      <div className="space-y-2 text-sm">
        {arcs.map((a, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
            <span className="text-neutral-700 dark:text-neutral-200 capitalize">
              {String(a[labelKey]).toLowerCase().replace(/_/g, " ")}
            </span>
            <span className="ml-auto pl-3 tabular-nums font-semibold text-neutral-800 dark:text-neutral-100">
              {a.val}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Revenue Sparkline ──────────────────────────────── */

function SparklineChart({ data = [], dateKey = "date", valueKey = "revenue" }) {
  if (data.length === 0)
    return (
      <p className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
        No revenue data yet.
      </p>
    );

  const vals = data.map((d) => Number(d[valueKey] || 0));
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;

  const W = 600;
  const H = 140;
  const padX = 4;
  const padY = 12;

  const points = vals.map((v, i) => {
    const x = padX + (i / Math.max(vals.length - 1, 1)) * (W - 2 * padX);
    const y = padY + ((max - v) / range) * (H - 2 * padY);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const polyline = points.join(" ");
  const lastPt = points[points.length - 1].split(",");
  const area = `${padX},${H} ${polyline} ${lastPt[0]},${H}`;

  return (
    <div className="min-w-0 max-w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full max-w-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#areaGrad)" />
        <polyline
          points={polyline}
          fill="none"
          stroke="#6366f1"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <div className="mt-1.5 flex min-w-0 justify-between gap-3 text-xs text-neutral-500 dark:text-neutral-400">
        <span className="min-w-0 max-w-[48%] truncate">{data[0]?.[dateKey]}</span>
        <span className="min-w-0 max-w-[48%] truncate text-right">{data[data.length - 1]?.[dateKey]}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────── */
/* Main Page                                               */
/* ─────────────────────────────────────────────────────── */

export default function BusinessAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await request({
        url: ROUTES.businessAnalytics,
        method: "GET",
        headers: { "X-Portal": "business" },
      });
      // handle { data: {...} } or flat shapes
      const payload = res?.data ?? res ?? null;
      setAnalytics(payload);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  /* ─ Memoised summary ─ */
  const summary = useMemo(
    () => (analytics?.summary ? analytics.summary : {}),
    [analytics]
  );

  const revenueChange = useMemo(
    () => pct(summary.monthRevenue, summary.lastMonthRevenue),
    [summary.monthRevenue, summary.lastMonthRevenue]
  );

  /* ─ KPI cards ─ */
  const statCards = useMemo(
    () => [
      {
        title: "Total Revenue",
        value: loading ? "—" : money(summary.totalRevenue),
        icon: DollarSign,
        hint: loading ? "" : pctLabel(revenueChange),
      },
      {
        title: "Total Orders",
        value: loading ? "—" : Number(summary.totalOrders || 0).toLocaleString(),
        icon: ShoppingBag,
      },
      {
        title: "Items Sold",
        value: loading ? "—" : Number(summary.totalSold || 0).toLocaleString(),
        icon: Package,
      },
      {
        title: "Avg Order Value",
        value: loading ? "—" : money(summary.avgOrderValue),
        icon: TrendingUp,
      },
    ],
    [loading, summary, revenueChange]
  );

  return (
    <div className="w-full min-w-0 max-w-full space-y-6">
      <PageHeader
        title="Sales analytics"
        description="Revenue, best-selling products, stock and order outcomes for the whole business."
        actions={
          <Button size="sm" icon={RefreshCw} loading={loading} onClick={fetchAnalytics}>
            Refresh
          </Button>
        }
      />

      {/* Error banner */}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/60 p-4 text-sm text-rose-800 dark:text-rose-200">
          {error}
        </div>
      )}

      {/* KPI stat cards */}
      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((c) => (
          <StatCard
            key={c.title}
            title={c.title}
            value={c.value}
            icon={c.icon}
            hint={c.hint}
          />
        ))}
      </div>

      {/* Revenue sparkline */}
      <Panel title="Revenue Trend (Last 30 Days)">
        {loading ? (
          <SkeletonBlock className="h-32 w-full" />
        ) : (
          <SparklineChart data={analytics?.dailyRevenue} dateKey="date" valueKey="revenue" />
        )}
      </Panel>

      {/* Charts grid */}
      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
        {/* Top by Revenue */}
        <Panel title="Top Products by Revenue">
          {loading ? (
            <div className="space-y-3">
              <SkeletonBlock className="h-4 w-full" />
              <SkeletonBlock className="h-4 w-11/12" />
              <SkeletonBlock className="h-4 w-10/12" />
              <SkeletonBlock className="h-4 w-9/12" />
            </div>
          ) : (
            <BarChart
              items={analytics?.topByRevenue}
              labelKey="name"
              valueKey="totalRevenue"
              formatValue={money}
            />
          )}
        </Panel>

        {/* Top by Qty */}
        <Panel title="Top Products by Quantity Sold">
          {loading ? (
            <div className="space-y-3">
              <SkeletonBlock className="h-4 w-full" />
              <SkeletonBlock className="h-4 w-11/12" />
              <SkeletonBlock className="h-4 w-10/12" />
            </div>
          ) : (
            <BarChart
              items={analytics?.topByQty}
              labelKey="name"
              valueKey="totalQty"
            />
          )}
        </Panel>

        {/* Revenue by Category */}
        <Panel title="Revenue by Category">
          {loading ? (
            <div className="space-y-3">
              <SkeletonBlock className="h-4 w-full" />
              <SkeletonBlock className="h-4 w-11/12" />
              <SkeletonBlock className="h-4 w-9/12" />
            </div>
          ) : (
            <BarChart
              items={analytics?.revenueByCategory}
              labelKey="category"
              valueKey="revenue"
              formatValue={money}
            />
          )}
        </Panel>

        {/* Order status doughnut */}
        <Panel title="Order Status Breakdown">
          {loading ? (
            <div className="flex items-center gap-4">
              <SkeletonBlock className="h-36 w-36 rounded-full" />
              <div className="flex-1 space-y-2">
                <SkeletonBlock className="h-3 w-24" />
                <SkeletonBlock className="h-3 w-20" />
                <SkeletonBlock className="h-3 w-28" />
              </div>
            </div>
          ) : (
            <DoughnutChart
              items={analytics?.statusBreakdown}
              labelKey="status"
              valueKey="count"
              ariaLabel="Order status chart"
            />
          )}
        </Panel>

        {/* Stock levels doughnut */}
        <Panel title="Stock Levels">
          {loading ? (
            <div className="flex items-center gap-4">
              <SkeletonBlock className="h-36 w-36 rounded-full" />
              <div className="flex-1 space-y-2">
                <SkeletonBlock className="h-3 w-24" />
                <SkeletonBlock className="h-3 w-20" />
                <SkeletonBlock className="h-3 w-28" />
              </div>
            </div>
          ) : (
            <DoughnutChart
              items={analytics?.stockLevels ? [
                { status: "Out of Stock", count: analytics.stockLevels.outOfStock },
                { status: "Low Stock (1-10)", count: analytics.stockLevels.lowStock },
                { status: "In Stock (>10)", count: analytics.stockLevels.inStock },
              ] : []}
              labelKey="status"
              valueKey="count"
              ariaLabel="Stock level chart"
            />
          )}
        </Panel>

        {/* Employee Performance */}
        <Panel title="Employee Performance (Products Added)">
          {loading ? (
            <div className="space-y-3">
              <SkeletonBlock className="h-4 w-full" />
              <SkeletonBlock className="h-4 w-11/12" />
              <SkeletonBlock className="h-4 w-9/12" />
            </div>
          ) : (
            <BarChart
              items={analytics?.employeePerformance}
              labelKey="employeeName"
              valueKey="productsCreated"
            />
          )}
        </Panel>
      </div>

      {/* Month-over-month comparison */}
      <Panel title="Month-over-Month Revenue">
        {loading ? (
          <SkeletonBlock className="h-20 w-full" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-4">
              <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                This Month
              </div>
              <div className="mt-1.5 text-2xl font-bold tabular-nums text-neutral-900 dark:text-neutral-50">
                {money(summary.monthRevenue)}
              </div>
            </div>
            <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-4">
              <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                Last Month
              </div>
              <div className="mt-1.5 text-2xl font-bold tabular-nums text-neutral-900 dark:text-neutral-50">
                {money(summary.lastMonthRevenue)}
              </div>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
